import {
  NAME_CHANGE_TYPES,
  NI_PROOF_TYPES,
  PHOTO_ID_TYPES,
  PROOF_ADDRESS_TYPES,
  RIGHT_TO_WORK_TYPES,
} from '@pgs/shared';
import { useState } from 'react';
import { useDocumentUpload } from '../hooks/useDocumentUpload';
import { NavButtons } from '../NavButtons';
import { StepPanel } from '../StepPanel';
import type { RegistrationDraft } from '../types';
import { cx } from '@/components/form/utils';

const EVISA_API_URL =
  (import.meta.env.VITE_EVISA_API_URL as string | undefined) ?? 'https://backend.seenem.co.uk';
const EVISA_COMPANY_NAME =
  (import.meta.env.VITE_EVISA_COMPANY_NAME as string | undefined) ?? 'PGS';

interface Props {
  initial: RegistrationDraft['documents'];
  initialDob?: string;
  onBack: () => void;
  onChange: (docs: RegistrationDraft['documents']) => void;
  onSubmit: (docs: RegistrationDraft['documents']) => void;
}

type Kind = 'photoId' | 'proofAddress' | 'rightToWork' | 'niProof' | 'nameChange';

interface Row {
  kind: Kind;
  label: string;
  types: readonly string[];
  optional: boolean;
}

export const Step7Documents = ({ initial, initialDob, onBack, onChange, onSubmit }: Props) => {
  const { upload, remove } = useDocumentUpload();
  const [docs, setDocs] = useState(initial);
  const [busy, setBusy] = useState<Kind | null>(null);
  const [error, setError] = useState<string>();

  const [evisa, setEvisa] = useState<{
    shareCode: string;
    dob: string;
    status?: string;
    message?: string;
    details?: Record<string, unknown>;
    passed?: boolean;
  }>({
    shareCode: initial.evisa?.shareCode ?? '',
    // Once an eVisa check has passed, lock to the DOB that GOV.UK validated.
    // Otherwise prefer the current Personal Details DOB so edits to Step 0
    // propagate forward.
    dob: initial.evisa?.passed
      ? (initial.evisa?.dob ?? initialDob ?? '')
      : (initialDob ?? initial.evisa?.dob ?? ''),
    passed: initial.evisa?.passed,
  });

  const evisaPassed = evisa.passed === true;

  const rows: Row[] = [
    { kind: 'photoId', label: 'Photo ID — Please Select', types: PHOTO_ID_TYPES, optional: evisaPassed },
    { kind: 'proofAddress', label: 'Proof of Address — Please Select', types: PROOF_ADDRESS_TYPES, optional: evisaPassed },
    { kind: 'rightToWork', label: 'Right to Work in the UK — Please Select', types: RIGHT_TO_WORK_TYPES, optional: evisaPassed },
    { kind: 'niProof', label: 'Proof of National Insurance Number — Please Select', types: NI_PROOF_TYPES, optional: true },
    { kind: 'nameChange', label: 'Name Change Documents — Please Select', types: NAME_CHANGE_TYPES, optional: true },
  ];

  const handleFile = async (row: Row, file: File) => {
    setBusy(row.kind);
    setError(undefined);
    try {
      const objectKey = await upload(row.kind, file);
      const selectedType = (document.getElementById(`${row.kind}-type`) as HTMLSelectElement)
        ?.value;
      const type = selectedType ?? row.types[0]!;
      setDocs((d) => {
        const next: RegistrationDraft['documents'] =
          row.kind === 'nameChange'
            ? { ...d, nameChange: [...(d.nameChange ?? []), { type, objectKey }] }
            : { ...d, [row.kind]: { type, objectKey } };
        onChange(next);
        return next;
      });
    } catch {
      setError('Upload failed. Try a smaller PDF or image.');
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async (kind: Kind, objectKey: string) => {
    setError(undefined);
    try {
      await remove(objectKey);
    } catch {
      // Even if S3 delete fails, we still clear the client state so the user
      // can continue. Orphaned S3 objects are cleaned up by the lifecycle rule.
    }
    setDocs((d) => {
      let next: RegistrationDraft['documents'];
      if (kind === 'nameChange') {
        next = {
          ...d,
          nameChange: (d.nameChange ?? []).filter((x) => x.objectKey !== objectKey),
        };
      } else {
        next = { ...d };
        delete next[kind];
      }
      onChange(next);
      return next;
    });
  };

  const runEvisa = async () => {
    if (!evisa.shareCode || !/^\d{2}\/\d{2}\/\d{4}$/.test(evisa.dob)) {
      setEvisa((e) => ({ ...e, message: 'Enter share code and DOB as dd/mm/yyyy.' }));
      return;
    }
    setEvisa((e) => ({ ...e, message: 'Checking share code with GOV.UK...' }));
    try {
      // Browser calls seenem directly. The seenem domain must include
      // https://pgsform.co.uk in its CORS Allow-Origin list.
      const upstream = await fetch(`${EVISA_API_URL}/validator/evisa/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shareCode: evisa.shareCode,
          dob: evisa.dob,
          companyName: EVISA_COMPANY_NAME,
        }),
      });
      const body = (await upstream.json().catch(() => ({}))) as {
        data?: { status?: string; reason?: string; details?: Record<string, unknown> };
        message?: string;
      };
      if (!upstream.ok) {
        setEvisa((e) => ({
          ...e,
          message: body.message ?? 'eVisa check failed',
          passed: false,
        }));
        return;
      }
      const payload = body.data ?? {};
      const passed = payload.status === 'Passed';
      setEvisa((e) => ({
        ...e,
        passed,
        status: payload.status ?? 'Unknown',
        details: payload.details,
        message: passed
          ? 'Passed — right to work confirmed. The uploads below are now optional.'
          : `${payload.status ?? 'Failed'}${payload.reason ? ' — ' + payload.reason : ''}`,
      }));
    } catch {
      setEvisa((e) => ({ ...e, message: 'Request failed. Try again.' }));
    }
  };

  // If eVisa has passed, the user may continue without uploading the three
  // primary docs. NI proof is always optional.
  const canContinue = evisaPassed
    ? true
    : (['photoId', 'proofAddress', 'rightToWork'] as const).every((k) => docs[k]?.objectKey);

  return (
    <StepPanel
      icon="fa-file-alt"
      title="Your Documents"
      description={
        <>
          <strong className="text-teal">What is required?</strong>
          <br />
          <br />
          <strong>ID</strong> — We need one form of ID. If you have an eVisa, enter the share code
          below; otherwise upload Photo ID, Proof of Address and Right to Work.
          <br />
          <br />
          <strong>NI Number Proof</strong> — Optional. P60, payslip, or NI card/letter if you have one.
        </>
      }
    >
      <label className="lbl" style={{ marginTop: 0 }}>
        eVisa Share Code
      </label>
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2.5">
        <input
          className="inp uppercase"
          placeholder="Share code"
          value={evisa.shareCode}
          onChange={(e) => setEvisa({ ...evisa, shareCode: e.target.value.toUpperCase() })}
        />
        <input
          className="inp"
          placeholder="DOB dd/mm/yyyy"
          maxLength={10}
          value={evisa.dob}
          onChange={(e) =>
            setEvisa({
              ...evisa,
              dob: e.target.value
                .replace(/\D/g, '')
                .slice(0, 8)
                .replace(/^(\d{2})(\d)/, '$1/$2')
                .replace(/^(\d{2}\/\d{2})(\d)/, '$1/$2'),
            })
          }
        />
        <button type="button" className="btn-lookup" onClick={runEvisa}>
          Validate
        </button>
      </div>
      {evisa.message && (
        <p
          className={`mt-1 text-[11px] font-semibold ${
            evisa.passed ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {evisa.message}
        </p>
      )}

      {rows.map((row) => {
        const uploaded =
          row.kind === 'nameChange'
            ? docs.nameChange ?? []
            : docs[row.kind]
              ? [docs[row.kind] as { type: string; objectKey: string }]
              : [];
        const optionalLabel =
          row.kind === 'niProof'
            ? '(optional)'
            : row.kind === 'nameChange'
              ? '(optional — only if your documents are in a different name)'
              : '(optional — covered by eVisa)';
        return (
          <div key={row.kind}>
            <label className="lbl">
              {row.label}
              {row.optional && (
                <span className="ml-2 text-[10px] font-normal normal-case tracking-normal text-ink-500">
                  {optionalLabel}
                </span>
              )}
            </label>
            <div className="mt-1.5 flex overflow-hidden rounded-sm border-[1.5px] border-ink-200">
              <select id={`${row.kind}-type`} className="flex-1 px-3 py-2.5 text-[12px] font-bold uppercase tracking-wide text-ink-500 outline-none">
                {row.types.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <label
                className={cx(
                  'flex cursor-pointer items-center gap-2 bg-navy px-4 py-2.5 text-[11px] font-bold uppercase tracking-wide text-white',
                  busy === row.kind && 'cursor-not-allowed opacity-60',
                )}
              >
                <i className="fas fa-cloud-upload-alt" />
                {busy === row.kind ? 'Uploading…' : 'Upload Files'}
                <input
                  type="file"
                  className="sr-only"
                  accept="application/pdf,image/png,image/jpeg,image/heic,image/webp"
                  disabled={busy === row.kind}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleFile(row, f);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            {uploaded.map((doc) => (
              <div
                key={doc.objectKey}
                className="mt-1 flex items-center justify-between text-[11px] text-green-600"
              >
                <span>Uploaded: {doc.type}</span>
                <button
                  type="button"
                  className="ml-2 text-[11px] font-semibold text-red-600 underline"
                  onClick={() => void handleRemove(row.kind, doc.objectKey)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        );
      })}
      {error && <p className="err-msg">{error}</p>}
      <NavButtons
        onBack={onBack}
        onNext={() =>
          onSubmit({
            ...docs,
            evisa:
              evisa.passed && evisa.shareCode
                ? {
                    shareCode: evisa.shareCode,
                    dob: evisa.dob,
                    passed: true,
                    details: evisa.details,
                    checkedAt: new Date().toISOString(),
                  }
                : undefined,
          })
        }
        nextDisabled={!canContinue}
      />
    </StepPanel>
  );
};
